import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';

/**
 * Auth guard to protect routes that require authentication.
 * Waits for permissions to load using the rxResource API.
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'calendar',
 *   component: CalendarComponent,
 *   canActivate: [authGuard]
 * }
 * ```
 */
export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  // Check if user is authenticated
  if (!authService.isAuthenticated()) {
    // Redirect to login page with return URL
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Wait for permissions to be loaded
  // Convert isLoaded signal to observable and wait for it to be true
  if (!permissionService.isLoaded()) {
    await firstValueFrom(toObservable(permissionService.isLoaded).pipe(filter(loaded => loaded)));
  }

  return true;
};
